import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Award, Users } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const coaches = [
  {
    name: "Ozan Aykut Tan",
    role: "Voleybol Baş Antrenörü",
    branch: "Voleybol",
    qualifications: ["2. Kademe Voleybol Antrenörü"],
    desc: "Kulübümüzün voleybol branşında sporcularımızın teknik ve taktik gelişiminden sorumlu baş antrenörümüz.",
    image: "/coach-ozan.png",
  },
  {
    name: "Buğra Şanlı",
    role: "Genel Koordinatör",
    branch: "Voleybol",
    qualifications: ["2. Kademe Voleybol Antrenörü"],
    desc: "Kulübümüzün genel koordinasyonunu yürüten ve voleybol branşında aktif görev alan antrenörümüz.",
    image: null,
  },
  {
    name: "Özge Uluca",
    role: "Sportif Direktör",
    branch: "Yönetim",
    qualifications: ["Sportif Direktör"],
    desc: "Kulübümüzün sportif faaliyetlerinin planlanması ve yönetiminden sorumlu direktörümüz.",
    image: null,
  },
  {
    name: "Zehra Yıldırım",
    role: "Cimnastik Antrenörü",
    branch: "Cimnastik",
    qualifications: ["1. Kademe Cimnastik Antrenörü"],
    desc: "Cimnastik branşımızda sporcularımızın esneklik, koordinasyon ve teknik gelişiminden sorumlu antrenörümüz.",
    image: "/coach-zehra.png",
  },
];

const branchColors = {
  "Voleybol": "bg-amber-500",
  "Basketbol": "bg-orange-500",
  "Cimnastik": "bg-pink-500",
  "Yönetim": "bg-burgundy",
};

export default function CoachesPage() {
  return (
    <div className="pt-16 lg:pt-20" data-testid="coaches-page">
      <SEOHead
        title="Antrenör Kadromuz - İRÜ Spor Kulübü"
        description="İRÜ Spor Kulübü deneyimli antrenör kadrosu. Voleybol, basketbol ve cimnastik branşlarında profesyonel eğitmenler."
        keywords="spor antrenörü Ataşehir, voleybol antrenörü Kadıköy, basketbol koçu İstanbul, cimnastik eğitmeni Maltepe"
      />

      {/* Hero */}
      <section className="relative py-24 lg:py-32 bg-[#1A1A1A]" data-testid="coaches-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-burgundy/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <span className="font-accent text-sm uppercase tracking-widest text-gold">Profesyonel Kadro</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mt-2">
            Antrenör Kadromuz
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/60 max-w-2xl leading-relaxed">
            Alanında uzman, lisanslı ve deneyimli antrenörlerimiz ile çocuklarınızın spor gelişimini en üst düzeye taşıyoruz.
          </p>
        </div>
      </section>

      {/* Coaches Grid */}
      <section className="py-20 lg:py-28 bg-white" data-testid="coaches-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coaches.map((coach, idx) => (
              <div
                key={idx}
                data-testid={`coach-card-${idx}`}
                className="border border-zinc-200 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-48 bg-zinc-100 flex items-center justify-center p-6 sm:p-0 shrink-0">
                    {coach.image ? (
                      <img
                        src={coach.image}
                        alt={coach.name}
                        className="w-32 h-32 sm:w-full sm:h-full object-cover rounded-full sm:rounded-none"
                      />
                    ) : (
                      <div className="w-32 h-32 sm:w-full sm:h-full bg-zinc-200 flex items-center justify-center rounded-full sm:rounded-none">
                        <Users className="w-12 h-12 text-zinc-400" strokeWidth={1} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-2 h-2 rounded-full ${branchColors[coach.branch] || "bg-zinc-400"}`} />
                      <span className="text-xs font-accent uppercase tracking-wider text-zinc-400">{coach.branch}</span>
                    </div>
                    <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-[#1A1A1A]">
                      {coach.name}
                    </h3>
                    <p className="text-sm text-burgundy font-medium mt-0.5">{coach.role}</p>
                    <p className="mt-3 text-sm text-zinc-500 leading-relaxed">{coach.desc}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {coach.qualifications.map((q) => (
                        <span key={q} className="inline-flex items-center gap-1 text-xs bg-zinc-100 text-zinc-600 px-2.5 py-1">
                          <Award className="w-3 h-3 text-burgundy" strokeWidth={1.5} />
                          {q}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-surface" data-testid="coaches-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Eğitim Felsefemiz</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
              Antrenörlerimizin Yaklaşımı
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Bireysel Gelişim", desc: "Her sporcunun yeteneklerini keşfeder ve bireysel gelişim planı oluştururuz." },
              { title: "Güvenli Ortam", desc: "Antrenmanlarımız kontrollü ve güvenli bir ortamda, modern tesislerimizde gerçekleştirilir." },
              { title: "Takım Ruhu", desc: "Sporun bireysel faydalarının yanında, takım çalışması ve sosyal becerilerin gelişimine önem veririz." },
            ].map((v) => (
              <div key={v.title} className="bg-white border border-zinc-200 p-8 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300">
                <h3 className="font-heading text-base font-bold uppercase tracking-wider text-[#1A1A1A]">{v.title}</h3>
                <p className="mt-3 text-sm text-zinc-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-burgundy text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">
            Deneyimli Kadromuzla Tanışın
          </h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            Çocuğunuz için en uygun branşı belirlemek ve antrenörlerimizle tanışmak için bize ulaşın.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/on-kayit">
              <Button className="bg-white text-burgundy hover:bg-gray-100 font-heading uppercase tracking-wider text-sm px-8 py-6">
                Ön Kayıt Ol <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-8 py-6">
                İletişime Geç
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export { coaches };
